import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';

import panelModes, { PanelMode } from './panelModes';
import useWidthBesideScrollbar from '../../hooks/useWidthBesideScrollbar';

export const DOCKED_PANEL_WIDTH = 420;

export const MIN_PAGE_WIDTH_BESIDE_DOCKED_PANEL = 762;

export const PANEL_MODE_STORAGE_KEY = 'pingwin.panelMode';

// Storage can be blocked or full, and the panel then opens floating as it did before.
const readStoredMode = (): PanelMode => {
  try {
    const stored = localStorage.getItem(PANEL_MODE_STORAGE_KEY);
    return stored === panelModes.DOCKED ? panelModes.DOCKED : panelModes.FLOATING;
  } catch {
    return panelModes.FLOATING;
  }
};

const storeMode = (mode: PanelMode) => {
  try {
    localStorage.setItem(PANEL_MODE_STORAGE_KEY, mode);
  } catch {
    // nothing to do
  }
};

type PinguDock = {
  open: boolean;
  setOpen: (open: boolean) => void;
  // Kept while there is no room to dock, so the panel docks again once there is.
  mode: PanelMode;
  setMode: (mode: PanelMode) => void;
  canDock: boolean;
  docked: boolean;
};

const PinguDockContext = createContext<PinguDock>({
  open: false,
  setOpen: () => {},
  mode: panelModes.FLOATING,
  setMode: () => {},
  canDock: false,
  docked: false,
});

// Wraps the page rather than living in the launcher, so the page narrows in the same
// render as the panel docks and floats, and the choice survives closing the panel.
// The choice is also kept in localStorage, so it survives a reload on this browser.
export const PinguDockContextProvider = ({
  sidebarWidth,
  children,
}: {
  sidebarWidth: number;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  // A remembered dock only applies while there is room for it, through canDock below.
  const [mode, setStateMode] = useState<PanelMode>(readStoredMode);
  const setMode = useCallback((nextMode: PanelMode) => {
    setStateMode(nextMode);
    storeMode(nextMode);
  }, []);
  const widthBesideScrollbar = useWidthBesideScrollbar();

  const canDock = widthBesideScrollbar - sidebarWidth - DOCKED_PANEL_WIDTH
    >= MIN_PAGE_WIDTH_BESIDE_DOCKED_PANEL;
  const docked = open && mode === panelModes.DOCKED && canDock;

  const value = useMemo(() => ({
    open,
    setOpen,
    mode,
    setMode,
    canDock,
    docked,
  }), [open, mode, setMode, canDock, docked]);

  return (
    <PinguDockContext.Provider value={value}>
      {children}
    </PinguDockContext.Provider>
  );
};

export const useDockContext = () => useContext(PinguDockContext);
